import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Box,
  Container,
  TextField,
  Button,
  Paper,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import InputAdornment from "@mui/material/InputAdornment";

import {
  adminFetchUsersWithRoles,
  adminAssignRole,
  adminRemoveRole,
  adminLockUser,
  adminDeleteUser,
  adminSetGlobalAdmin,
  fetchAllTenants,
} from "../api/apiClient";
import { useTenant } from "../contexts/TenantContext";
import UserImportDialog from "../components/userImport/UserImportDialog";

// ADMIN is included — a tenant-scoped Admin may grant it too, but only
// within their own tenant (enforced by managedTenantIds below and by the
// backend). GLOBAL_ADMIN is not a tenant role: a Global Admin grants it with
// the separate "Global Admin" toggle on each user.
const ASSIGNABLE_ROLES = [
  "STUDENT",
  "INSTRUCTOR",
  "COURSE_COORDINATOR",
  "AGENCY_ADMIN",
  "ADMIN",
];

export default function AdminUserRolesPage() {
  const { user } = useTenant();
  const isGlobalAdmin = !!user?.roles?.includes("GLOBAL_ADMIN");
  const isTenantAdmin = !isGlobalAdmin && !!user?.roles?.includes("ADMIN");
  // Tenants this Admin actually administers (from the full per-tenant role
  // map, not just the currently-active tenant). A tenant-scoped Admin can
  // never assign/view roles outside this set.
  const managedTenantIds = isGlobalAdmin
    ? null
    : Object.entries(user?.tenantRoles || {})
        .filter(([, roles]) => Array.isArray(roles) && roles.includes("ADMIN"))
        .map(([tenantId]) => tenantId);
  const assignableRoles = ASSIGNABLE_ROLES;
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Assign dialog state
  const [assignDialog, setAssignDialog] = useState({
    open: false,
    userId: null,
    username: "",
  });
  const [assignTenant, setAssignTenant] = useState("");
  const [assignRole, setAssignRole] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback((q) => {
    setLoading(true);
    setError(null);
    adminFetchUsersWithRoles(q)
      .then(setUsers)
      .catch((e) =>
        setError(e?.response?.data?.message || "Failed to load users."),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load("");
    fetchAllTenants()
      .then(setTenants)
      .catch(() => {});
  }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search.trim());
  };

  const handleRemoveRole = (userId, tenantId, role) => {
    if (!window.confirm(`Remove role "${role}" from this user in ${tenantId}?`))
      return;
    adminRemoveRole(userId, tenantId, role)
      .then(() => load(search.trim()))
      .catch((e) =>
        setError(e?.response?.data?.message || "Failed to remove role."),
      );
  };

  const handleToggleLock = (u) => {
    const nextLocked = !u.locked;
    const action = nextLocked ? "lock" : "unlock";
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} account for ${u.username}?`))
      return;
    adminLockUser(u.id, nextLocked)
      .then(() => load(search.trim()))
      .catch((e) => setError(e?.response?.data?.message || `Failed to ${action} user.`));
  };

  const handleDeleteUser = (u) => {
    if (!window.confirm(`Permanently delete user "${u.username}"? This cannot be undone.`))
      return;
    adminDeleteUser(u.id)
      .then(() => load(search.trim()))
      .catch((e) => setError(e?.response?.data?.message || "Failed to delete user."));
  };

  const handleToggleGlobalAdmin = (u, grant) => {
    const msg = grant
      ? `Make ${u.username} a Global Admin? They will have full access to every training center.`
      : `Remove Global Admin access from ${u.username}?`;
    if (!window.confirm(msg)) return;
    adminSetGlobalAdmin(u.id, grant)
      .then(() => load(search.trim()))
      .catch((e) =>
        setError(e?.response?.data?.message || "Failed to update Global Admin access."),
      );
  };

  const openAssign = (userId, username) => {
    setAssignDialog({ open: true, userId, username });
    // A tenant-scoped Admin only ever administers their own tenant(s) — lock
    // it in rather than making them pick from a list that's filtered to just
    // that anyway.
    setAssignTenant(
      !isGlobalAdmin && managedTenantIds?.length === 1 ? managedTenantIds[0] : "",
    );
    setAssignRole("");
    setAssignError(null);
  };

  const handleAssign = () => {
    if (!assignTenant || !assignRole) {
      setAssignError("Please select both a tenant and a role.");
      return;
    }
    setAssignLoading(true);
    setAssignError(null);
    adminAssignRole(assignDialog.userId, assignTenant, assignRole)
      .then(() => {
        setAssignDialog({ open: false, userId: null, username: "" });
        load(search.trim());
      })
      .catch((e) =>
        setAssignError(e?.response?.data?.message || "Failed to assign role."),
      )
      .finally(() => setAssignLoading(false));
  };

  // Guard: only ADMIN/GLOBAL_ADMIN should be here (routing should already
  // handle this)
  if (!isGlobalAdmin && !isTenantAdmin) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          Access denied. This page requires the Admin role.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <ManageAccountsIcon sx={{ fontSize: 36, color: "primary.main" }} />
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ fontFamily: "var(--font-display)" }}>
            User Role Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search users and manage their per-tenant role assignments across all
            training centers.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<GroupAddIcon />}
          onClick={() => setImportOpen(true)}
          sx={{ ml: "auto" }}
        >
          Import Users
        </Button>
      </Box>

      {/* Search Bar */}
      <Paper
        component="form"
        onSubmit={handleSearch}
        elevation={0}
        variant="outlined"
        sx={{ display: "flex", gap: 1.5, p: 2, mb: 3, alignItems: "center" }}
      >
        <TextField
          placeholder="Search by name, username, or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" type="submit" disabled={loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Search"}
        </Button>
        {search && (
          <Button
            variant="outlined"
            onClick={() => {
              setSearch("");
              load("");
            }}
          >
            Clear
          </Button>
        )}
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* User List */}
      {!loading && users.length === 0 && (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ p: 4, textAlign: "center" }}
        >
          <Typography color="text.secondary">No users found.</Typography>
        </Paper>
      )}

      {!loading &&
        users.map((u) => {
          const tenantEntries = Object.entries(u.tenantRoles || {});
          const hasTenantRoles = tenantEntries.length > 0;
          const uniqueRoles = [
            ...new Set([
              ...(u.globalRoles || []),
              ...tenantEntries.flatMap(([, roles]) => roles),
            ]),
          ];

          return (
            <Accordion
              key={u.id}
              disableGutters
              elevation={0}
              variant="outlined"
              sx={{
                mb: 1.5,
                "&:before": { display: "none" },
                borderRadius: "8px",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography fontWeight={700}>
                      {u.fullName || u.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {u.email} &nbsp;·&nbsp; ID: {u.id}
                    </Typography>
                  </Box>
                  {/* Unique role types across global + all tenant assignments */}
                  <Box
                    sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", ml: 1 }}
                  >
                    {uniqueRoles.map((r) => (
                      <Chip
                        key={r}
                        label={r.replace(/_/g, " ")}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: "0.65rem", height: 20 }}
                      />
                    ))}
                    {u.locked && (
                      <Chip
                        label="Locked"
                        size="small"
                        color="error"
                        sx={{ fontSize: "0.65rem", height: 20 }}
                      />
                    )}
                  </Box>
                  {/* Tenant role summary + actions */}
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5, mr: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      {hasTenantRoles
                        ? `${tenantEntries.length} tenant${tenantEntries.length !== 1 ? "s" : ""}`
                        : "No tenant roles"}
                    </Typography>
                    {isGlobalAdmin && (
                      <IconButton
                        size="small"
                        title={u.locked ? "Unlock account" : "Lock account"}
                        onClick={(e) => { e.stopPropagation(); handleToggleLock(u); }}
                        sx={{ color: u.locked ? "success.main" : "warning.main" }}
                      >
                        {u.locked ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                      </IconButton>
                    )}
                    {isGlobalAdmin && (
                      <IconButton
                        size="small"
                        title="Delete user"
                        onClick={(e) => { e.stopPropagation(); handleDeleteUser(u); }}
                        sx={{ color: "error.main" }}
                      >
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2}>
                  {/* Tenant roles table */}
                  {hasTenantRoles ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Tenant</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Roles</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tenantEntries.map(([tenantId, roles]) => {
                          const tenantName =
                            tenants.find((t) => String(t.id) === tenantId)?.name ||
                            tenantId;
                          return (
                            <TableRow key={tenantId}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                  {tenantName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {tenantId}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {roles.map((role) => (
                                    <Chip
                                      key={role}
                                      label={role.replace(/_/g, " ")}
                                      size="small"
                                      color="success"
                                      onDelete={() =>
                                        handleRemoveRole(u.id, tenantId, role)
                                      }
                                      deleteIcon={<DeleteIcon />}
                                      sx={{ fontSize: "0.7rem" }}
                                    />
                                  ))}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  title="Assign additional role"
                                  onClick={() => {
                                    openAssign(u.id, u.username || u.email);
                                    setAssignTenant(tenantId);
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      This user has no per-tenant role assignments yet.
                    </Typography>
                  )}

                  {/* Pending invitations */}
                  {u.pendingInvitations && u.pendingInvitations.length > 0 && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                      >
                        PENDING INVITATIONS
                      </Typography>
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {u.pendingInvitations.map((inv) => (
                          <Typography
                            key={inv.id}
                            variant="caption"
                            color="text.secondary"
                          >
                            · {inv.role} in {inv.tenantId} (sent{" "}
                            {inv.createdAt
                              ? new Date(inv.createdAt).toLocaleDateString()
                              : "—"}
                            )
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Assign role button */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => openAssign(u.id, u.username || u.email)}
                    >
                      Assign Role
                    </Button>
                    {/* GLOBAL_ADMIN is a global role, not per tenant; only a
                        Global Admin may grant or revoke it, and never their own. */}
                    {isGlobalAdmin &&
                      (u.globalRoles?.includes("GLOBAL_ADMIN") ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          data-testid={`revoke-global-admin-${u.id}`}
                          disabled={String(u.id) === String(user?.id)}
                          onClick={() => handleToggleGlobalAdmin(u, false)}
                        >
                          Remove Global Admin
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          data-testid={`grant-global-admin-${u.id}`}
                          onClick={() => handleToggleGlobalAdmin(u, true)}
                        >
                          Make Global Admin
                        </Button>
                      ))}
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

      <UserImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => load(search.trim())}
        tenants={tenants}
        isGlobalAdmin={isGlobalAdmin}
        managedTenantIds={managedTenantIds}
      />

      {/* Assign Role Dialog */}
      <Dialog
        open={assignDialog.open}
        onClose={() =>
          setAssignDialog({ open: false, userId: null, username: "" })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>Assign Role to {assignDialog.username}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {assignError && <Alert severity="error">{assignError}</Alert>}
            {!isGlobalAdmin && managedTenantIds?.length === 1 ? (
              // A tenant-scoped Admin can only ever assign roles in their own
              // tenant — show it as a fixed fact, not a picker into other tenants.
              <TextField
                label="Training Center (Tenant)"
                value={
                  tenants.find((t) => String(t.id) === managedTenantIds[0])?.name ||
                  managedTenantIds[0]
                }
                size="small"
                fullWidth
                disabled
              />
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel>Training Center (Tenant)</InputLabel>
                <Select
                  value={assignTenant}
                  label="Training Center (Tenant)"
                  onChange={(e) => setAssignTenant(e.target.value)}
                >
                  {tenants
                    .filter((t) => !managedTenantIds || managedTenantIds.includes(String(t.id)))
                    .map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={assignRole}
                label="Role"
                onChange={(e) => setAssignRole(e.target.value)}
              >
                {assignableRoles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r.replace(/_/g, " ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setAssignDialog({ open: false, userId: null, username: "" })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={assignLoading}
          >
            {assignLoading ? "Assigning…" : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
